import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import loanDetailsService from "../../services/LoanDetailsService";


// ------------------- Surety ----------------------------------

export const fetchSuretiesByLoan = createAsyncThunk(
  "loanDetails/fetchSuretiesByLoan",
  async (loanNo, { rejectWithValue }) => {
    try {
      const response = await loanDetailsService.GetSuretyByLoanNo(loanNo);
      const statusCode = response?.data?.StatusCode ?? response?.StatusCode;
      if (statusCode !== 200) {
        throw new Error(response?.data?.Message || "Failed to load sureties.");
      }
      return response.data?.ResultSet || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message || error.message || "Failed to load sureties."
      );
    }
  }
);

export const addSurety = createAsyncThunk(
  "loanDetails/addSurety",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await loanDetailsService.AddSurety(payload);
      const statusCode = response?.data?.StatusCode ?? response?.StatusCode;
      if (statusCode !== 200) {
        throw new Error(response?.data?.Message || "Failed to add surety.");
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message || error.message || "Failed to add surety."
      );
    }
  }
);

export const updateSurety = createAsyncThunk(
  "loanDetails/updateSurety",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await loanDetailsService.UpdateSurety(payload);
      const statusCode = response?.data?.StatusCode ?? response?.StatusCode;
      if (statusCode !== 200) {
        throw new Error(response?.data?.Message || "Failed to update surety.");
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message || error.message || "Failed to update surety."
      );
    }
  }
);

export const deleteSurety = createAsyncThunk(
  "loanDetails/deleteSurety",
  async (suretyId, { rejectWithValue }) => {
    try {
      const response = await loanDetailsService.DeleteSurety(suretyId);
      const statusCode = response?.data?.StatusCode ?? response?.StatusCode;
      if (statusCode !== 200) {
        throw new Error(response?.data?.Message || "Failed to delete surety.");
      }
      return suretyId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message || error.message || "Failed to delete surety."
      );
    }
  }
);

// --------------------- Loan Documents ---------------------------------

export const fetchLoanDocs = createAsyncThunk(
  "loanDetails/fetchLoanDocs",
  async (loanNo, { rejectWithValue }) => {
    try {
      const response = await loanDetailsService.ListLoanDocs(loanNo);
      return response.data?.ResultSet || response.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message || error.message || "Failed to load loan documents."
      );
    }
  }
);

export const deleteLoanDoc = createAsyncThunk(
  "loanDetails/deleteLoanDoc",
  async ({ loanNo, docType }, { rejectWithValue }) => {
    try {
      await loanDetailsService.DeleteLoanDoc(loanNo, docType);
      return docType;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message || error.message || "Failed to delete document."
      );
    }
  }
);

// ---------- Loan Types ---------------------------
export const fetchAllLoanTypes = createAsyncThunk(
  "loanDetails/fetchAllLoanTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await loanDetailsService.GetAllLoanTypes();
      const statusCode = response?.data?.StatusCode ?? response?.StatusCode;
      if (statusCode !== 200) {
        throw new Error(response?.data?.Message || "Failed to load loan types.");
      }
      const raw = response.data?.ResultSet || [];
      return raw.map((item) => ({
        id: item.HLTM_ID,
        categoryCode: item.HLTM_CATEGORY_CODE, // 'M' | 'O'
        categoryName: item.HLTM_CATEGORY_CODE === "O" ? "Other Loan" : "Loan",
        typeName: item.HLTM_TYPE_NAME,
        description: item.HLTM_DESCRIPTION || "",
        isActive: item.HLTM_IS_ACTIVE === true || item.HLTM_IS_ACTIVE === "True",
        status: (item.HLTM_IS_ACTIVE === true || item.HLTM_IS_ACTIVE === "True") ? "Active" : "Inactive",
      }));
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message || error.message || "Failed to load loan types."
      );
    }
  }
);

export const addLoanType = createAsyncThunk(
  "loanDetails/addLoanType",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await loanDetailsService.AddLoanType(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message || error.message || "Failed to add loan type."
      );
    }
  }
);

export const updateLoanType = createAsyncThunk(
  "loanDetails/updateLoanType",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await loanDetailsService.UpdateLoanType(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message || error.message || "Failed to update loan type."
      );
    }
  }
);

const initialState = {
  sureties: [],
  documents: [],
  loanTypes: [],
  loadingLoanTypes: false,
  savingLoanTypes: false,
  loadingSureties: false,
  loadingDocs: false,
  savingSurety: false,
  error: null,
};

const loanDetailsSlice = createSlice({
  name: "loanDetails",
  initialState,
  reducers: {
    clearLoanDetails: (state) => {
      state.sureties = [];
      state.documents = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Sureties
      .addCase(fetchSuretiesByLoan.pending, (state) => {
        state.loadingSureties = true;
      })
      .addCase(fetchSuretiesByLoan.fulfilled, (state, action) => {
        state.loadingSureties = false;
        state.sureties = action.payload;
      })
      .addCase(fetchSuretiesByLoan.rejected, (state, action) => {
        state.loadingSureties = false;
        state.error = action.payload;
      })
      .addCase(addSurety.pending, (state) => {
        state.savingSurety = true;
      })
      .addCase(addSurety.fulfilled, (state) => {
        state.savingSurety = false;
      })
      .addCase(addSurety.rejected, (state, action) => {
        state.savingSurety = false;
        state.error = action.payload;
      })
      .addCase(updateSurety.pending, (state) => {
        state.savingSurety = true;
      })
      .addCase(updateSurety.fulfilled, (state) => {
        state.savingSurety = false;
      })
      .addCase(updateSurety.rejected, (state, action) => {
        state.savingSurety = false;
        state.error = action.payload;
      })

      // Loan Types
      .addCase(fetchAllLoanTypes.pending, (state) => {
        state.loadingLoanTypes = true;
      })
      .addCase(fetchAllLoanTypes.fulfilled, (state, action) => {
        state.loadingLoanTypes = false;
        state.loanTypes = action.payload;
      })
      .addCase(fetchAllLoanTypes.rejected, (state, action) => {
        state.loadingLoanTypes = false;
        state.error = action.payload;
      })
      .addCase(addLoanType.pending, (state) => {
        state.savingLoanType = true;
      })
      .addCase(addLoanType.fulfilled, (state) => {
        state.savingLoanType = false;
      })
      .addCase(addLoanType.rejected, (state, action) => {
        state.savingLoanType = false;
        state.error = action.payload;
      })
      .addCase(updateLoanType.pending, (state) => {
        state.savingLoanType = true;
      })
      .addCase(updateLoanType.fulfilled, (state) => {
        state.savingLoanType = false;
      })
      .addCase(updateLoanType.rejected, (state, action) => {
        state.savingLoanType = false;
        state.error = action.payload;
      });
  },
});

export const { clearLoanDetails } = loanDetailsSlice.actions;
export default loanDetailsSlice.reducer;